import { Search, X } from 'lucide-react';
import { useDeferredValue, useEffect, useRef, useState } from 'react';
import { usePosts } from '../lib/api/posts';
import { fmtRelative } from '../lib/date';
import type { PostListItem } from '../lib/types';
import { plainText } from '../lib/utils';
import { Input } from './ui/input';
import { Spinner } from './ui/spinner';

/**
 * Search-as-you-type post picker. Shows recent posts when the search box is
 * focused but empty, switches to live search results once the editor types
 * (debounced via React's `useDeferredValue`).
 *
 * Used by:
 *  - BreakingNews → Compose (pick the post to broadcast)
 *  - Eyewitness review dialog → Publish (link the report to a published post)
 *
 * Selection collapses the input into a confirmation chip with a clear button.
 * `value` is the slug of the currently-selected post (the source of truth);
 * `onChange` fires with `(slug, title)` so the parent can render attribution
 * elsewhere if it wants.
 */
export function PostPicker({
  type,
  locale,
  value,
  onChange,
  placeholder,
}: {
  type: 'post' | 'page';
  locale: string;
  value: string;
  onChange: (slug: string, title: string) => void;
  placeholder?: string;
}): JSX.Element {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [selectedTitle, setSelectedTitle] = useState('');
  const deferredQuery = useDeferredValue(query);
  const ref = useRef<HTMLDivElement>(null);

  const recent = usePosts({ locale, type, pageSize: 5, sort: 'newest' });
  const search = usePosts({ locale, type, q: deferredQuery, pageSize: 8, enabled: !!deferredQuery });

  const items: PostListItem[] = deferredQuery
    ? (search.data?.items ?? [])
    : (recent.data?.items ?? []);
  const isLoading = deferredQuery ? search.isLoading : recent.isLoading;

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const select = (post: PostListItem) => {
    onChange(post.slug, plainText(post.title));
    setSelectedTitle(plainText(post.title));
    setQuery('');
    setOpen(false);
  };

  const clear = () => {
    onChange('', '');
    setSelectedTitle('');
    setQuery('');
  };

  if (value && selectedTitle) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-brand/30 bg-brand/5 px-3 py-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-ink">{selectedTitle}</p>
          <p className="font-mono text-[11px] text-ink-subtle">{value}</p>
        </div>
        <button
          type="button"
          onClick={clear}
          className="shrink-0 rounded p-0.5 text-ink-subtle hover:text-ink"
          title="Clear selection"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle" />
        <Input
          className="pl-8"
          placeholder={placeholder ?? 'Search posts or pick from recent…'}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
        />
      </div>

      {open ? (
        <div className="absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-lg border border-line bg-surface shadow-elevated">
          <p className="border-b border-line px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-subtle">
            {deferredQuery ? 'Search results' : 'Recent posts'}
          </p>
          {isLoading ? (
            <div className="flex justify-center py-4">
              <Spinner label="Loading…" />
            </div>
          ) : items.length === 0 ? (
            <p className="px-3 py-3 text-sm text-ink-muted">No posts found.</p>
          ) : (
            <ul>
              {items.map((post) => (
                <li key={post.id}>
                  <button
                    type="button"
                    onClick={() => select(post)}
                    className="flex w-full items-start gap-3 px-3 py-2.5 text-left hover:bg-surface-tinted"
                  >
                    {post.featuredImage?.url ? (
                      <img
                        src={post.featuredImage.url}
                        alt=""
                        className="h-10 w-14 shrink-0 rounded object-cover"
                      />
                    ) : null}
                    <div className="min-w-0 flex-1">
                      <p className="line-clamp-1 text-sm font-medium text-ink">
                        {plainText(post.title)}
                      </p>
                      <p className="flex items-center gap-1.5 font-mono text-[11px] text-ink-subtle">
                        <span>{post.slug}</span>
                        <span aria-hidden>·</span>
                        <span>{fmtRelative(post.publishedAt)}</span>
                      </p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
